import React from 'react';
import { Printer } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { useTranslation } from '../i18n';
import { categories, Category } from '../data/priceData';
function CategoryBlock({
  category,
  lang
}: {
  category: Category;
  lang: 'en' | 'ua' | 'ru' | 'ro';
}) {
  return <div className="mb-2.5 break-inside-avoid" data-id="element-3105" data-mp="zngytm4n4x2j">
      <div className="bg-forest-700 text-white text-[8.5px] font-bold uppercase tracking-wider px-2 py-1 mb-1" data-id="element-3106" data-mp="zl49gnwjzkfg">
        {category.title[lang]}
      </div>
      <div className="space-y-[3px] px-0.5" data-id="element-3107" data-mp="18mu03rim2fc6">
        {category.items.map((item, idx) => <div key={idx} className="flex items-baseline gap-0.5" data-id="element-3108" data-mp="1dec1ggojqhxo" data-mpi={idx}>
            <span className="text-[9px] text-charcoal leading-tight flex-shrink-0" style={{
          maxWidth: '72%'
        }} data-id="element-3109" data-mp="1idox9qxeazce">
              {item.name[lang]}
            </span>
            <span className="flex-grow border-b border-dotted border-beige-dark/60 mx-0.5 relative top-[-1px]" data-id="element-3110" data-mp="1scxpae8pa3w4" />
            <span className="text-[9px] font-bold text-forest-700 whitespace-nowrap tabular-nums flex-shrink-0" data-id="element-3111" data-mp="2d69tyw1aw1kz">
              {item.price}
            </span>
          </div>)}
      </div>
    </div>;
}
export function PrintPricePage() {
  const {
    lang
  } = useTranslation();
  const handlePrint = () => window.print();
  const today = new Date().toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
  const currentLang = lang as 'en' | 'ua' | 'ru' | 'ro';
  return <div className="min-h-screen bg-gray-100 p-6 print:p-0 print:bg-white" data-id="element-3112" data-mp="2qxewq6zyt9zj">
      {/* Controls */}
      <div className="no-print max-w-[210mm] mx-auto mb-6 flex items-center justify-between" data-id="element-3113" data-mp="2m4c35yv6u0jc">
        <div data-id="element-3114" data-mp="2i41w0ivnt42w">
          <h1 className="text-xl font-bold text-gray-900" data-id="element-3115" data-mp="2i2h4710qbz8q">
            Dentico Clinic — Price List
          </h1>
          <p className="text-sm text-gray-500 mt-0.5" data-id="element-3116" data-mp="3eoapp7g5qb8d">
            Tip: Enable <strong data-id="element-3117" data-mp="39u6h6jr8tv56">Background graphics</strong> in print settings
            for best results.
          </p>
        </div>
        <Button onClick={handlePrint} className="gap-2 shadow-md" data-id="element-3118" data-mp="180a8750k12j9">
          <Printer className="w-4 h-4" data-id="element-3119" data-mp="1cvz4mzha23bc" />
          Print Price List
        </Button>
      </div>

      {/* A4 Page — fixed height to match exactly one A4 */}
      <div className="price-page bg-white mx-auto shadow-xl print:shadow-none flex flex-col" style={{
      width: '210mm',
      height: '297mm'
    }} data-id="element-3120" data-mp="1377fez3lpx44">
        {/* Compact Header — matches website header brand lockup */}
        <div className="border-b-2 border-forest-700 px-8 pt-5 pb-3 flex-shrink-0" data-id="element-3121" data-mp="u83vxu0ivfp3">
          <div className="flex items-center justify-between" data-id="element-3122" data-mp="fb30pgj4h2lv">
            <div className="flex items-center gap-3" data-id="element-3123" data-mp="k837xurpkbek">
              {/* Brand wordmark — architectural lockup, forest palette */}
              <div className="flex flex-col leading-none" data-id="element-3124" data-mp="29oug6wo4ugp8">
                <span className="text-[26px] font-semibold tracking-[0.22em] text-forest-700" style={{
                fontFamily: '"Playfair Display", Georgia, serif'
              }} data-id="element-3125" data-mp="2en5tjhdmz9c7">
                  DENTICO
                </span>
                <span className="block text-right text-[8px] tracking-[0.4em] text-forest-700 uppercase mt-0.5" data-id="element-3126" data-mp="cou0w86ocoso">
                  Clinic
                </span>
              </div>
              <div className="border-l border-beige-dark/60 pl-3" data-id="element-3127" data-mp="7wkev38ok8vo">
                <div className="text-[9px] text-charcoal-light uppercase tracking-widest leading-relaxed" data-id="element-3128" data-mp="lo7cb6eczng1">
                  Private Dentistry · London, Angel
                  <br data-id="element-3129" data-mp="vohkgclje4oc" />
                  360–364 City Road, EC1V 2PY
                </div>
              </div>
            </div>
            <div className="text-right" data-id="element-3130" data-mp="qq6cy8ips69a">
              <div className="text-xl font-bold text-forest-900 uppercase tracking-wide leading-none" data-id="element-3131" data-mp="2xfb87n5ohs7f">
                Price List
              </div>
              <div className="text-[9px] text-stone-500 mt-1" data-id="element-3132" data-mp="2shjh2vcuya4a">
                {today} · denticoclinic.co.uk
              </div>
            </div>
          </div>
        </div>

        {/* Two-column price grid — fills remaining space */}
        <div className="flex-grow px-8 pt-4 pb-2 grid grid-cols-2 gap-x-6 items-start overflow-hidden" data-id="element-3133" data-mp="2yykw7yi6vdx8">
          <div data-id="element-3134" data-mp="33tqkv52z76p0">
            {categories.slice(0, Math.ceil(categories.length / 2)).map((cat, idx) => <CategoryBlock key={idx} category={cat} lang={currentLang} data-id="element-3135" data-mp="2b7p1asifs2lr" data-mpi={idx} />)}
          </div>
          <div data-id="element-3136" data-mp="2g71xi3v76m10">
            {categories.slice(Math.ceil(categories.length / 2)).map((cat, idx) => <CategoryBlock key={idx} category={cat} lang={currentLang} data-id="element-3137" data-mp="2l3sd6q33bflw" data-mpi={idx} />)}
          </div>
        </div>

        {/* Compact Footer */}
        <div className="flex-shrink-0 mx-8 pt-2 pb-4 border-t border-gray-200" data-id="element-3138" data-mp="1sgxrxqyg8c7d">
          <p className="text-[8px] text-gray-400 text-center leading-relaxed" data-id="element-3139" data-mp="1xf94wak1j2tt">
            Prices are indicative and may vary. A consultation is required
            before treatment. &nbsp;·&nbsp; +44 7345 197820 (WhatsApp)
            &nbsp;·&nbsp; denticoclinic.co.uk &nbsp;·&nbsp; Book:
            denticoclinic.patientportal.onl/bookings
          </p>
        </div>
      </div>

      <style data-id="element-3140" data-mp="5lj9ayur6e7h">{`
        @media print {
          @page {
            size: A4 portrait;
            margin: 0;
          }
          html, body {
            margin: 0;
            padding: 0;
            background: white;
          }
          .no-print {
            display: none !important;
          }
          .price-page {
            width: 210mm !important;
            height: 297mm !important;
            box-shadow: none !important;
            margin: 0 !important;
            overflow: hidden !important;
          }
        }
      `}</style>
    </div>;
}